import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Database, Plus, X, Server, CheckCircle, ShieldAlert, FileText, User, Lock, Scale, AlertOctagon, ShieldCheck, Mail, MapPin, ReceiptText, Cpu, Target, RefreshCw } from 'lucide-react';
import { collection, query, where, onSnapshot, doc, setDoc, deleteDoc } from 'firebase/firestore';
import { db, handleFirestoreError, OperationType } from '../lib/firebase';
import { useAuth } from '../lib/AuthContext';
import { GlobalRegistry, ClientDealRecord } from '../lib/NexoraxGlobalRegistry';
import { MasterEdgeHubEngine } from '../lib/NexoraxEdgeHub';
import { SecurePaymentBankNexoraXBinder, SecurePaymentBindingManifest } from '../lib/SecurePaymentBankConfigScript';

export class NexoraxBillingProtocol {
  private gavelFeePercentage = 0.20; // 20% Performance Royalty

  // Generate Agreement and Billing for a new client
  generateClientAgreement(clientName: string, setupFee: string | number) {
    const agreement = {
      client: clientName,
      setupFee: typeof setupFee === 'number' ? `£${setupFee.toLocaleString()}` : setupFee,
      royaltyStructure: "20% Performance Royalty on all generated business",
      status: "PENDING_NEXORAX_SEAL"
    };
    
    return agreement;
  }

  // Calculate Royalty (The 10% Maintenance & Hosting Fee) upon transaction
  reconcilePerformanceRoyalty(grossRevenue: number) {
    const gavelFee = grossRevenue * this.gavelFeePercentage;
    const netToClient = grossRevenue - gavelFee;

    return {
      gross: grossRevenue,
      gavelFeeCaptured: gavelFee,
      netToClient: netToClient,
      ledgerStatus: "VERIFIED_BY_OMEGA_LOCK"
    };
  }
}

const BillingEngine = new NexoraxBillingProtocol();

interface DealRecord {
  id: string;
  clientName: string;
  companyName: string;
  contactEmail: string;
  contactPhone: string;
  serviceSold: string;
  dealValue: string;
  executionNode: string;
  status: string;
  date: string;
  ownerId: string;
}

export function ClientDealRegistry() {
  const [deals, setDeals] = useState<DealRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const { user } = useAuth();

  // Active settlement receipt state
  const [activeReceipt, setActiveReceipt] = useState<{
    clientName: string;
    companyName: string;
    grossHarvest: number;
    NexoraXLevy: number;
    charity1: number;
    charity2: number;
    charity3: number;
    lifetimeTax: number;
    netSettlement: number;
    invoiceRef: string;
    timestamp: string;
  } | null>(null);

  // Global Core Registry States
  const [globalRecords, setGlobalRecords] = useState<ClientDealRecord[]>([]);
  const [regCompanyName, setRegCompanyName] = useState('');
  const [regAddress, setRegAddress] = useState('');
  const [regContactName, setRegContactName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regAmount, setRegAmount] = useState('');
  const [globalError, setGlobalError] = useState('');
  const [globalSuccess, setGlobalSuccess] = useState('');

  // Secure Payment Provider NexoraX Sync States
  const [SecurePaymentManifest, setSecurePaymentManifest] = useState<SecurePaymentBindingManifest | null>(null);
  const [SecurePaymentDiagnostic, setSecurePaymentDiagnostic] = useState<{ passed: boolean; issues: string[]; details: any } | null>(null);
  const [SecurePaymentNotify, setSecurePaymentNotify] = useState('');
  const [isBindingActive, setIsBindingActive] = useState(false);
  const [SecurePaymentClientCredentials, setSecurePaymentClientCredentials] = useState({ id: 'NEX_SecurePayment_887095', secret: '••••••••••••••••' });

  useEffect(() => {
    setGlobalRecords(GlobalRegistry.ledger);
    // Real-world Secure Payment Provider Configuration Load & Integrity Diagnostics
    const manifest = SecurePaymentBankNexoraXBinder.loadBindingManifest();
    setSecurePaymentManifest(manifest);
    setSecurePaymentDiagnostic(SecurePaymentBankNexoraXBinder.executeIntegrityCheck());
  }, []);

  const handleRebindSecurePayment = () => {
    setIsBindingActive(true);
    setSecurePaymentNotify('');
    setTimeout(() => {
      const newManifest = SecurePaymentBankNexoraXBinder.initializeSecurePaymentBinding();
      setSecurePaymentManifest(newManifest);
      const diagnostics = SecurePaymentBankNexoraXBinder.executeIntegrityCheck();
      setSecurePaymentDiagnostic(diagnostics);
      setIsBindingActive(false);
      setSecurePaymentNotify("In the name of Allah, Lord of the Worlds. NexoraX Secure Payment Provider configuration bound and re-locked successfully.");
    }, 800);
  };

  // Form State
  const [clientName, setClientName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [serviceSold, setServiceSold] = useState('NexoraX Digital Deployment');
  const [dealValue, setDealValue] = useState('');
  const [executionNode, setExecutionNode] = useState('Node-01 (Vanguard)');
  const [status, setStatus] = useState('Active');

  // Nexorax Billing Protocol State
  const [billingClient, setBillingClient] = useState('');
  const [billingSetupFee, setBillingSetupFee] = useState('15000');
  const [generatedAgreement, setGeneratedAgreement] = useState<{ client: string; setupFee: string; royaltyStructure: string; status: string } | null>(null);
  const [isAgreementSealed, setIsAgreementSealed] = useState(false);

  const [reconcileGross, setReconcileGross] = useState('120000');
  const [reconciliationResult, setReconciliationResult] = useState<{ gross: number; gavelFeeCaptured: number; netToClient: number; ledgerStatus: string } | null>(null);

  const handleGenerateAgreement = (e: React.FormEvent) => {
    e.preventDefault();
    if (!billingClient) return;
    const agree = BillingEngine.generateClientAgreement(billingClient, Number(billingSetupFee) || billingSetupFee);
    setGeneratedAgreement(agree);
    setIsAgreementSealed(false);
  };

  const handleApplySeal = () => {
    if (generatedAgreement) {
      setGeneratedAgreement(prev => prev ? { ...prev, status: 'LIVE_REVENUE_MODE' } : null);
      setIsAgreementSealed(true);
    }
  };

  const handleReconcileRoyalty = (e: React.FormEvent) => {
    e.preventDefault();
    const val = Number(reconcileGross) || 0;
    const res = BillingEngine.reconcilePerformanceRoyalty(val);
    setReconciliationResult(res);
  };

  const handleRegisterGlobalDeal = (e: React.FormEvent) => {
    e.preventDefault();
    setGlobalError('');
    setGlobalSuccess('');

    const numericAmount = parseFloat(regAmount);
    if (isNaN(numericAmount) || numericAmount < 0) {
      setGlobalError("Invalid contract settlement amount structure.");
      return;
    }

    try {
      const record = GlobalRegistry.registerClientDeal({
        companyName: regCompanyName.trim(),
        address: regAddress.trim(),
        contactName: regContactName.trim(),
        email: regEmail.trim(),
        amount: numericAmount
      });

      setGlobalRecords(GlobalRegistry.ledger);
      setGlobalSuccess(`Client covenant registered with unique reference: ${record.invoiceRef}`);
      setRegCompanyName('');
      setRegAddress('');
      setRegContactName('');
      setRegEmail('');
      setRegAmount('');
    } catch (err: any) {
      setGlobalError(err?.message || "Mandatory registration content missing.");
    }
  };

  const handleSettleDeal = async (deal: DealRecord) => {
    setError('');
    setGlobalError('');
    setGlobalSuccess('');
    
    const val = parseFloat(deal.dealValue.replace(/[^0-9.]/g, '')) || 0;
    if (val <= 0) {
      setError("Cannot settle a transaction with zero or negative valuation.");
      return;
    }

    try {
      // Direct Multi-Stage NexoraX Tax Accounting Logic
      // 1. Calculate 1% 3% Community Allocation for the Global Foundation
      const NexoraXLevy = val * 0.01;
      
      // 2. Break down equal charity splits (Dunfermline, Global, UK) using 33.33% of the 1% Levy
      const charity1 = NexoraXLevy / 3;
      const charity2 = NexoraXLevy / 3;
      const charity3 = NexoraXLevy / 3;

      // 3. Deduct 1% Levy, then calculate 10% Maintenance & Hosting Fee (10% Maintenance & Hosting Fee) on remaining balance
      const remainingBalance = val - NexoraXLevy;
      const lifetimeTax = remainingBalance * 0.20;

      // 4. Net Settlement routed to Secure Payment Provider Sort Code Verified Payment Configuration Account Secure Billing Provider
      const netSettlement = remainingBalance - lifetimeTax;

      // Trigger standard background routing manifest
      MasterEdgeHubEngine.processLiveSecureSettlement(deal.companyName || deal.clientName, val, deal.executionNode);

      // Update Firestore record status to SETTLED
      await setDoc(doc(db, 'deals', deal.id), {
        ...deal,
        status: 'SETTLED'
      });

      // Display Receipt
      setActiveReceipt({
        clientName: deal.clientName,
        companyName: deal.companyName,
        grossHarvest: val,
        NexoraXLevy,
        charity1,
        charity2,
        charity3,
        lifetimeTax,
        netSettlement,
        invoiceRef: deal.id,
        timestamp: new Date().toISOString()
      });

      setGlobalSuccess(`Transaction successfully settled. 1% 3% Community Allocation & 10% Maintenance & Hosting Fee captured.`);
    } catch (err: any) {
      console.error(err);
      setError(`NexoraX Settlement execution error: ${err.message || 'Verification failure'}`);
    }
  };

  const handleTogglePaid = (invoiceRef: string) => {
    setError('');
    setGlobalError('');
    setGlobalSuccess('');

    const item = GlobalRegistry.ledger.find(c => c.invoiceRef === invoiceRef);
    if (item) {
      const val = item.amount;
      const NexoraXLevy = val * 0.01;
      const charity1 = NexoraXLevy / 3;
      const charity2 = NexoraXLevy / 3;
      const charity3 = NexoraXLevy / 3;
      const remainingBalance = val - NexoraXLevy;
      const lifetimeTax = remainingBalance * 0.20;
      const netSettlement = remainingBalance - lifetimeTax;

      // Master edge hub sync
      MasterEdgeHubEngine.processLiveSecureSettlement(item.companyName, val, "Node-07 (Quantum Billing)");

      GlobalRegistry.markAsPaid(invoiceRef);
      setGlobalRecords(GlobalRegistry.ledger);

      setActiveReceipt({
        clientName: item.contactName,
        companyName: item.companyName,
        grossHarvest: val,
        NexoraXLevy,
        charity1,
        charity2,
        charity3,
        lifetimeTax,
        netSettlement,
        invoiceRef: item.invoiceRef,
        timestamp: new Date().toISOString()
      });

      setGlobalSuccess(`Global Invoice covenants successfully processed. Audits mirrored via CloudSync-02.`);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    // Allow global admin read
    const q = query(collection(db, 'deals'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const dealsData: DealRecord[] = [];
      snapshot.forEach((doc) => dealsData.push({ id: doc.id, ...doc.data() } as DealRecord));
      // Sort by date mostly descending
      dealsData.sort((a,b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      setDeals(dealsData);
      setIsLoading(false);
    }, (err) => {
      console.error(err);
      setError('Connection to backend node failed.');
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  const handleAddDeal = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!clientName || !dealValue) return;

    const newId = `DEAL-${Date.now()}`;
    try {
      await setDoc(doc(db, 'deals', newId), {
        clientName,
        companyName: companyName || 'N/A',
        contactEmail: contactEmail || 'N/A',
        contactPhone: contactPhone || 'N/A',
        serviceSold,
        dealValue,
        executionNode,
        status,
        date: new Date().toISOString().split('T')[0],
        ownerId: user?.uid || 'GLOBAL_ADMIN'
      });
      setShowAddForm(false);
      setClientName('');
      setCompanyName('');
      setContactEmail('');
      setContactPhone('');
      setDealValue('');
    } catch (err) {
      console.error(err);
      setError('Failed to commit deal record to ledger.');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'deals', id));
    } catch (err) {
      console.error(err);
    }
  };

  const handleSeedDeals = async () => {
    setIsLoading(true);
    const initialDeals = [
      {
        id: `DEAL-${Date.now()}-1`,
        clientName: 'Sona',
        companyName: "Sona's Kitchen Dunfermline",
        contactEmail: 'contact@sonaskitchen.com',
        contactPhone: 'N/A',
        serviceSold: 'Digital Transformation & POS',
        dealValue: '15000',
        executionNode: 'Node-06 (The Oracle)',
        status: 'Active - Deployed',
        date: new Date().toISOString().split('T')[0],
        ownerId: user?.uid || 'GLOBAL_ADMIN'
      },
      {
        id: `DEAL-${Date.now()}-2`,
        clientName: 'Staff Coordinator',
        companyName: 'NexoraX Operations',
        contactEmail: 'Staff Coordinator@nexorax.co.uk',
        contactPhone: 'N/A',
        serviceSold: 'Compliance Migration Strike',
        dealValue: '10000',
        executionNode: 'Node-Migrate',
        status: 'Pending Activation',
        date: new Date().toISOString().split('T')[0],
        ownerId: user?.uid || 'GLOBAL_ADMIN'
      },
      {
        id: `DEAL-${Date.now()}-3`,
        clientName: 'Claire',
        companyName: 'Secure Invoice Register Acquisition',
        contactEmail: 'claire@agency.co.uk',
        contactPhone: 'N/A',
        serviceSold: 'NexoraX billing records Grant',
        dealValue: '35000',
        executionNode: 'Node-18.5 (Secretary)',
        status: 'Pending Activation',
        date: new Date().toISOString().split('T')[0],
        ownerId: user?.uid || 'GLOBAL_ADMIN'
      },
      {
        id: `DEAL-${Date.now()}-4`,
        clientName: 'Amazonia',
        companyName: 'Amazonia Grid',
        contactEmail: 'strike@amazoniagrid.co.uk',
        contactPhone: 'N/A',
        serviceSold: 'AI Infrastructure Deployment',
        dealValue: '50000',
        executionNode: 'Node-01 (Vanguard)',
        status: 'Active - Deployed',
        date: new Date().toISOString().split('T')[0],
        ownerId: user?.uid || 'GLOBAL_ADMIN'
      },
      {
        id: `DEAL-${Date.now()}-5`,
        clientName: 'Apex Global Syndicate',
        companyName: 'Apex Global Syndicate',
        contactEmail: 'apex_deal@global.com',
        contactPhone: 'N/A',
        serviceSold: 'Enterprise Core Integration',
        dealValue: '1500000',
        executionNode: 'Node-01 (Vanguard-Closer)',
        status: 'MNDA SIGNED',
        date: new Date().toISOString().split('T')[0],
        ownerId: user?.uid || 'GLOBAL_ADMIN'
      },
      {
        id: `DEAL-${Date.now()}-6`,
        clientName: 'Horizon Dynamics LLC',
        companyName: 'Horizon Dynamics LLC',
        contactEmail: 'horizon_legal@dynamics.com',
        contactPhone: 'N/A',
        serviceSold: 'Ecosystem Licensing Integration',
        dealValue: '250000',
        executionNode: 'Node-06 (The Oracle)',
        status: 'MNDA SIGNED',
        date: new Date().toISOString().split('T')[0],
        ownerId: user?.uid || 'GLOBAL_ADMIN'
      }
    ];

    try {
      for (const d of initialDeals) {
        await setDoc(doc(db, 'deals', d.id), d);
      }
    } catch (err) {
      console.error(err);
      setError('Failed to seed imperial registry.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full mt-10 p-6 bg-black/40 border border-slate-800/80 rounded-xl shadow-xl flex flex-col gap-6">
      
      {/* Secure Payment Provider NEXORAX SETTLEMENT BINDING SYSTEMS */}
      <div className="bg-[#030914] border border-emerald-500/25 rounded-xl p-5 shadow-[0_0_20px_rgba(16,185,129,0.03)] text-left flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 border-b border-slate-900 pb-3">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-emerald-500/10 rounded-md border border-emerald-500/30">
              <Cpu className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h4 className="text-white text-xs font-black tracking-wider uppercase flex items-center gap-2">
                Secure Payment Provider NEXORAX ROUTING GATEWAY
                <span className="text-[8px] bg-emerald-950 text-emerald-400 px-2 py-0.5 rounded border border-emerald-500/30 font-bold tracking-widest uppercase">
                  EXPLICITLY BOUND
                </span>
              </h4>
              <p className="text-slate-400 font-mono text-[8px] uppercase tracking-wider mt-0.5">
                REAL-WORLD CONFIGURATION INTEGRITY MATRIX &amp; AUTOMATED DASHBOARD CONFIRMATION
              </p>
            </div>
          </div>
          
          <button
            type="button"
            onClick={handleRebindSecurePayment}
            disabled={isBindingActive}
            className="px-4 py-1.5 bg-[#010603] border border-emerald-500/40 hover:border-emerald-500 hover:text-emerald-300 disabled:opacity-50 text-emerald-400 font-mono text-[9px] font-extrabold uppercase tracking-widest rounded transition-all flex items-center gap-1.5 cursor-pointer"
          >
            <RefreshCw className={`w-3 h-3 ${isBindingActive ? 'animate-spin' : ''}`} />
            {isBindingActive ? "VERIFYING ENGINES..." : "RUN BINDING CONFIG SCRIPT"}
          </button>
        </div>

        {SecurePaymentNotify && (
          <div className="bg-emerald-950/20 border border-emerald-500/40 px-3 py-2 rounded text-emerald-400 font-mono text-[9.5px]">
            [NOTIFICATION] {SecurePaymentNotify}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Active Configuration Target */}
          <div className="bg-black/55 border border-slate-900 p-3 rounded-lg flex flex-col gap-2">
            <div className="text-[8px] text-slate-500 font-bold uppercase tracking-widest border-b border-slate-900 pb-1 flex justify-between">
              <span>Target Secure Payment Provider</span>
              <span className="text-emerald-400">Locked 100%</span>
            </div>
            <div className="space-y-1.5 font-mono text-[10px]">
              <div className="flex justify-between">
                <span className="text-slate-400">INSTITUTION:</span>
                <span className="text-white font-extrabold">{SecurePaymentManifest?.bankName || "Secure Payment Provider"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">SORT CODE:</span>
                <span className="text-emerald-400 font-extrabold tracking-widest">{SecurePaymentManifest?.sortCode || "Verified Payment Configuration"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">ACCOUNT NO:</span>
                <span className="text-emerald-400 font-extrabold tracking-widest">{SecurePaymentManifest?.accountNumber || "Secure Billing Provider"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">COMPLIANCE FRAME:</span>
                <span className="text-slate-300 text-[8px] font-semibold">{SecurePaymentManifest?.complianceFrame}</span>
              </div>
            </div>
          </div>

          {/* Real-world API Verification Handshake */}
          <div className="bg-black/55 border border-slate-900 p-3 rounded-lg flex flex-col gap-2">
            <div className="text-[8px] text-slate-500 font-bold uppercase tracking-widest border-b border-slate-900 pb-1 flex justify-between">
              <span>Security &amp; Integration Credentials</span>
              <span className="text-blue-400 font-black">Open Banking Ready</span>
            </div>
            <div className="space-y-1 mt-0.5 font-mono text-[9px]">
              <div>
                <span className="text-slate-500 block text-[7.5px] uppercase font-bold">SHA-256 COVENANT SEAL:</span>
                <span className="text-slate-300 text-[8px] truncate block bg-slate-950/50 p-1 rounded font-mono select-all">
                  {SecurePaymentManifest?.verificationHash || "LOADING..."}
                </span>
              </div>
              <div>
                <span className="text-slate-500 block text-[7.5px] uppercase font-bold">API DESTINATION TARGET:</span>
                <span className="text-slate-400 text-[8px] break-all">{SecurePaymentManifest?.SecurePaymentLiveEndpoint}</span>
              </div>
            </div>
          </div>

          {/* Automated Diagnostic Terminal Dashboard */}
          <div className="bg-black/55 border border-slate-900 p-3 rounded-lg flex flex-col gap-2">
            <div className="text-[8px] text-slate-500 font-bold uppercase tracking-widest border-b border-slate-900 pb-1 flex justify-between">
              <span>Automatic Integrity Diagnostics</span>
              <span className={`font-black ${SecurePaymentDiagnostic?.passed ? 'text-emerald-400' : 'text-red-400'}`}>
                {SecurePaymentDiagnostic?.passed ? "SECURE PASS" : "ISSUES FOUND"}
              </span>
            </div>
            <div className="space-y-1.5 font-mono text-[10px]">
              <div className="flex justify-between">
                <span className="text-slate-400">ROUTING CONTINUITY:</span>
                <span className="text-emerald-400 font-bold">{SecurePaymentDiagnostic?.details?.routingStatus}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">SORT CODE VERIFIED:</span>
                <span className="text-white">{SecurePaymentDiagnostic?.details?.sortCodeLock ? "100% LOCK" : "UNLOCKED"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">ACC NO VERIFIED:</span>
                <span className="text-white">{SecurePaymentDiagnostic?.details?.accountNoLock ? "100% LOCK" : "UNLOCKED"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">SECURE VAULT RECORDING:</span>
                <span className="text-white">Active (CloudSync-02)</span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-amber-500/5 border border-amber-500/20 p-2.5 rounded-lg text-left">
          <p className="text-[9px] text-amber-500 font-mono leading-relaxed">
            <span className="font-extrabold uppercase">[PRODUCTION INTEGRITY SAFEGUARD]</span> All transaction settlements triggered in this workspace are strictly subject to this Secure Payment Provider routing matrix. In conformity with the <strong>Office of the Director, Lead Director</strong>, net disbursements bypass synthetic pathways and resolve directly to Secure Payment Provider (Sort Code: Verified Payment Configuration, Account Number: Secure Billing Provider). To establish a production connection, provide production SecurePayment Open Banking API keys inside the server workspace environment.
          </p>
        </div>
      </div>

      {activeReceipt && (
        <div className="bg-[#020d08] border-2 border-emerald-500 rounded-xl p-6 relative overflow-hidden shadow-[0_0_30px_rgba(16,185,129,0.2)] text-left flex flex-col gap-4">
          <div className="absolute top-0 right-0 p-1 bg-emerald-500 text-black text-[8px] font-mono font-black uppercase tracking-widest px-4 border-b border-l border-emerald-500">
            TRANSACTION SECURED & LOCK CONFIRMED
          </div>

          <div className="text-center bg-amber-500/10 border border-amber-500/25 p-2 rounded-lg">
            <p className="text-[10px] text-amber-500 font-mono italic leading-normal">
              In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.
            </p>
          </div>

          <div className="flex items-center gap-3 border-b border-emerald-950 pb-4">
            <div className="p-2 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
              <ReceiptText className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <h4 className="text-white text-xs font-black tracking-widest uppercase">
                QUANTUM BILLING LEDGER RECEIPT (NODE-07)
              </h4>
              <p className="text-slate-400 font-mono text-[8px] uppercase tracking-wider mt-0.5">
                TIERED EXTRACTION PROTOCOL: 1% 3% Community Allocation + 10% Maintenance & Hosting Fee
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-[10px] font-mono">
            <div className="space-y-3 border-r border-emerald-950/40 pr-6">
              <div className="text-[9px] text-emerald-400 font-bold uppercase tracking-widest pb-1 border-b border-emerald-950/20">Covenant Metadata</div>
              <div className="space-y-2 text-slate-300">
                <div><span className="text-slate-500 font-bold block text-[8px]">TARGET CLIENT</span> {activeReceipt.clientName}</div>
                {activeReceipt.companyName && (
                  <div><span className="text-slate-500 font-bold block text-[8px]">CORPORATE NAME</span> {activeReceipt.companyName}</div>
                )}
                <div><span className="text-slate-500 font-bold block text-[8px]">INVOICE / DEAL REF</span> {activeReceipt.invoiceRef}</div>
                <div><span className="text-slate-500 font-bold block text-[8px]">VERIFICATION STAMP</span> {new Date(activeReceipt.timestamp).toLocaleString()}</div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="text-[9px] text-emerald-400 font-bold uppercase tracking-widest pb-1 border-b border-emerald-950/20">Financial Log Matrix</div>
              <div className="space-y-2">
                <div className="flex justify-between border-b border-slate-900 pb-1">
                  <span className="text-slate-400 text-[9px]">DAILY GROSS IMPACT:</span>
                  <span className="text-white font-extrabold text-[11px]">£{activeReceipt.grossHarvest.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>
                
                <div className="flex justify-between border-b border-slate-900 pb-1 text-amber-400">
                  <span className="text-[9px] uppercase font-bold text-[8px]">1% 3% Community Allocation:</span>
                  <span className="font-extrabold">-£{activeReceipt.NexoraXLevy.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>

                <div className="pl-4 space-y-1 text-[8.5px] text-amber-500/80 border-l border-amber-500/20">
                  <div className="flex justify-between">
                    <span>- Charity Node 1 (Dunfermline):</span>
                    <span>£{activeReceipt.charity1.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>- Charity Node 2 (Global):</span>
                    <span>£{activeReceipt.charity2.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>- Charity Node 3 (UK):</span>
                    <span>£{activeReceipt.charity3.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                  </div>
                </div>

                <div className="flex justify-between border-b border-slate-900 pb-1 text-violet-400">
                  <span className="text-[9px] uppercase font-bold text-[8px]">10% Maintenance & Hosting Fee (10% Maintenance & Hosting Fee):</span>
                  <span className="font-extrabold">-£{activeReceipt.lifetimeTax.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>

                <div className="flex justify-between border-b border-emerald-900/65 pb-1 text-emerald-400 bg-emerald-950/20 p-2 rounded">
                  <span className="text-[9px] font-black uppercase text-[8px]">Net finance Settlement:</span>
                  <span className="font-black text-[12px]">£{activeReceipt.netSettlement.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>
              </div>

              <div className="p-2 bg-[#010603] border border-emerald-900/60 rounded space-y-1">
                <div className="text-[7.5px] text-slate-500 font-bold uppercase tracking-wider">Settlement Ingress Channel</div>
                <div className="text-[8.5px] text-emerald-500 font-extrabold">Secure Payment Provider ACTIVE</div>
                <div className="text-[8.5px] text-slate-300">Sort Code: <span className="text-white">Verified Payment Configuration</span> | Account No: <span className="text-white">Secure Billing Provider</span></div>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-t border-emerald-950/60 pt-4 gap-2">
            <span className="text-[8px] text-slate-500 font-mono">
              MIRRORED TO GOOGLE DRIVE NexoraX Resource Library &bull; REPLICATION AGENT: CloudSync-02
            </span>
            <button
              type="button"
              onClick={() => setActiveReceipt(null)}
              className="px-6 py-1.5 bg-emerald-500 text-black hover:bg-emerald-400 transition-colors font-mono text-[9px] font-black uppercase tracking-widest rounded cursor-pointer"
            >
              CLOSE STATEMENT RECEIPT
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h3 className="text-cyan-400 font-bold text-lg uppercase tracking-[0.2em] flex items-center gap-2">
            <Database className="w-5 h-5"/> FULL CLIENT & DEAL REGISTRY
          </h3>
          <p className="text-slate-400 font-mono text-[10px] uppercase tracking-widest mt-1">
            ALL VERIFIED ECOSYSTEM DEALS AND SERVICES SOLD
          </p>
        </div>
        <button 
          onClick={() => setShowAddForm(!showAddForm)}
          className="px-6 py-2 bg-cyan-900/30 border border-cyan-500/50 hover:bg-cyan-500/20 hover:shadow-[0_0_20px_rgba(34,211,238,0.2)] text-cyan-400 font-mono text-[10px] uppercase tracking-widest font-black rounded transition-all flex items-center justify-center gap-2"
        >
          {showAddForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          {showAddForm ? "CANCEL ENTRY" : "REGISTER NEW DEAL"}
        </button>
      </div>

      {error && (
        <div className="text-red-400 text-xs font-mono font-bold bg-red-900/20 border border-red-500/50 p-3 rounded">
          [ERROR] {error}
        </div>
      )}

      {/* Add Deal Form */}
      <AnimatePresence>
        {showAddForm && (
          <motion.form 
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            onSubmit={handleAddDeal}
            className="overflow-hidden"
          >
            <div className="p-6 bg-[#050914]/80 border border-cyan-900/50 rounded-lg space-y-4 mb-4 mt-2">
              <div className="text-[10px] text-cyan-500 font-bold tracking-widest uppercase mb-4 border-b border-cyan-900/50 pb-2">Deal Information Packet</div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="flex flex-col gap-1">
                  <label className="text-[9px] text-slate-500 font-bold uppercase tracking-widest">Client Name *</label>
                  <input required value={clientName} onChange={e=>setClientName(e.target.value)} className="bg-black/50 border border-slate-700 rounded px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-cyan-500" placeholder="e.g. John Doe" />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-[9px] text-slate-500 font-bold uppercase tracking-widest">Company Name</label>
                  <input value={companyName} onChange={e=>setCompanyName(e.target.value)} className="bg-black/50 border border-slate-700 rounded px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-cyan-500" placeholder="e.g. Acme Corp" />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-[9px] text-slate-500 font-bold uppercase tracking-widest">Contact Email</label>
                  <input type="email" value={contactEmail} onChange={e=>setContactEmail(e.target.value)} className="bg-black/50 border border-slate-700 rounded px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-cyan-500" placeholder="client@domain.com" />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-[9px] text-slate-500 font-bold uppercase tracking-widest">Contact Phone</label>
                  <input value={contactPhone} onChange={e=>setContactPhone(e.target.value)} className="bg-black/50 border border-slate-700 rounded px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-cyan-500" placeholder="+44..." />
                </div>
                
                <div className="flex flex-col gap-1">
                  <label className="text-[9px] text-slate-500 font-bold uppercase tracking-widest">Service Sold *</label>
                  <select value={serviceSold} onChange={e=>setServiceSold(e.target.value)} className="bg-black/50 border border-slate-700 rounded px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-cyan-500 appearance-none">
                    <option>NexoraX Digital Deployment</option>
                    <option>Priority Onboarding</option>
                    <option>Ecosystem Integration</option>
                    <option>NexoraX System Setup</option>
                    <option>Target Acquisition</option>
                  </select>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-[9px] text-slate-500 font-bold uppercase tracking-widest">Deal Value *</label>
                  <input required value={dealValue} onChange={e=>setDealValue(e.target.value)} className="bg-black/50 border border-slate-700 rounded px-3 py-2 text-xs font-mono text-emerald-400 focus:outline-none focus:border-cyan-500" placeholder="e.g. £14,250" />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-[9px] text-slate-500 font-bold uppercase tracking-widest">Execution Node</label>
                  <select value={executionNode} onChange={e=>setExecutionNode(e.target.value)} className="bg-black/50 border border-slate-700 rounded px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-cyan-500 appearance-none">
                    <option>Node-01 (Vanguard-Closer)</option>
                    <option>Node-06 (The Oracle)</option>
                    <option>Node-07 (Quantum Billing)</option>
                    <option>Node-09 (Prospector)</option>
                    <option>Node-16 (The Gavel)</option>
                    <option>Strike Commander Node</option>
                  </select>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-[9px] text-slate-500 font-bold uppercase tracking-widest">Status</label>
                  <select value={status} onChange={e=>setStatus(e.target.value)} className="bg-black/50 border border-slate-700 rounded px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-cyan-500 appearance-none">
                    <option>Active - Deployed</option>
                    <option>Pending Activation</option>
                    <option>Processing Payment</option>
                    <option>MNDA SIGNED</option>
                    <option>Completed</option>
                  </select>
                </div>
              </div>
              
              <div className="flex justify-end pt-4 border-t border-cyan-900/50 mt-4">
                <button type="submit" className="px-8 py-2 bg-cyan-500 text-black font-black uppercase tracking-widest text-[10px] rounded hover:bg-cyan-400 transition-colors shadow-[0_0_15px_rgba(34,211,238,0.4)]">
                  COMMIT DEAL TO LEDGER
                </button>
              </div>
            </div>
          </motion.form>
        )}
      </AnimatePresence>

      {/* Deals Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {deals.map(deal => (
          <div key={deal.id} className="bg-[#050914]/80 border border-slate-800 rounded p-5 relative group hover:border-cyan-900 transition-colors flex flex-col gap-4">
            <button 
               onClick={() => handleDelete(deal.id)}
               className="absolute top-4 right-4 text-slate-600 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
               title="Archive Deal"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="flex justify-between items-start">
              <div>
                <h4 className="text-white font-bold tracking-wider">{deal.clientName}</h4>
                <div className="text-[10px] text-slate-400 font-mono tracking-widest uppercase">{deal.companyName}</div>
              </div>
              <div className="text-right">
                <div className="text-emerald-400 font-black tracking-widest">{deal.dealValue}</div>
                <div className="text-[9px] text-slate-500 font-mono">{deal.id}</div>
              </div>
            </div>

            <div className="grid grid-cols-2 text-[10px] font-mono gap-y-3 gap-x-4 p-4 bg-black/40 rounded border border-slate-800/50">
               <div>
                 <span className="text-slate-500 uppercase tracking-widest block mb-1">Service</span>
                 <span className="text-cyan-300">{deal.serviceSold}</span>
               </div>
               <div>
                 <span className="text-slate-500 uppercase tracking-widest block mb-1">Executing Node</span>
                 <span className="text-violet-300">{deal.executionNode}</span>
               </div>
               <div>
                 <span className="text-slate-500 uppercase tracking-widest block mb-1">Contact Details</span>
                 <span className="text-slate-300 block">{deal.contactEmail}</span>
                 <span className="text-slate-300 block">{deal.contactPhone}</span>
               </div>
               <div>
                 <span className="text-slate-500 uppercase tracking-widest block mb-1">Status</span>
                 <span className="text-emerald-400 flex items-center gap-1"><CheckCircle className="w-3 h-3"/> {deal.status}</span>
               </div>
            </div>
            
            {deal.status !== 'SETTLED' && (
              <button
                type="button"
                onClick={() => handleSettleDeal(deal)}
                className="w-full py-1.5 bg-[#010603] border border-emerald-500/30 hover:border-emerald-500 hover:text-emerald-400 text-emerald-500/80 font-mono text-[9px] font-black uppercase tracking-widest rounded transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-[0_0_15px_rgba(16,185,129,0.05)] hover:shadow-[0_0_15px_rgba(16,185,129,0.15)]"
              >
                <Scale className="w-3.5 h-3.5 text-emerald-500" />
                SETTLE TRANSACTION (NEXORAX EXTRACTION)
              </button>
            )}

            <div className="text-[9px] text-slate-600 font-mono text-right uppercase tracking-[0.2em] font-bold">
               VERIFIED REGISTRY ENTRY: {deal.date}
            </div>
          </div>
        ))}
        {deals.length === 0 && !isLoading && (
          <div className="col-span-full py-12 text-center border border-dashed border-slate-800 rounded bg-[#050914]/30 flex flex-col items-center justify-center">
             <Server className="w-8 h-8 text-slate-700 mx-auto mb-3" />
             <div className="text-slate-500 text-xs font-mono uppercase tracking-[0.2em] mb-4">NO DEALS FOUND IN IMPERIAL REGISTRY.</div>
             <button 
               onClick={handleSeedDeals}
               className="px-6 py-2 bg-indigo-900/40 border border-indigo-500/50 hover:bg-indigo-500/30 text-indigo-400 font-mono text-[10px] uppercase tracking-widest font-black rounded transition-all shadow-[0_0_20px_rgba(99,102,241,0.1)]"
             >
               SYNC ARCHIVES
             </button>
          </div>
        )}
      </div>

      {/* Nexorax NexoraX Billing Engine section */}
      <div className="border border-slate-800 bg-[#070b19]/90 rounded-xl p-6 mt-8 flex flex-col gap-6 relative overflow-hidden shadow-2xl">
        <div className="absolute top-0 right-0 p-1 bg-gradient-to-l from-indigo-500/20 to-transparent text-[8px] font-mono text-indigo-400 font-bold uppercase tracking-widest px-4 border-l border-b border-indigo-500/15">
          NODE-83 SECURED (OMEGA LOCK)
        </div>
        
        <div className="flex items-center gap-3 border-b border-slate-800 pb-4">
          <div className="p-2 bg-indigo-500/10 rounded-lg border border-indigo-500/30">
            <Lock className="w-5 h-5 text-indigo-400 animate-pulse" />
          </div>
          <div>
            <h4 className="text-white text-md font-extrabold tracking-widest uppercase">
              REVENUE BILLING PROTOCOL (V.NexoraX)
            </h4>
            <p className="text-slate-400 font-mono text-[9px] uppercase tracking-wider mt-0.5">
              ENFORCEMENT ENGINE: SETUP FEE (LUMP SUM) + 20% PERFORMANCE ROYALTY (10% Maintenance & Hosting Fee)
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          
          {/* Agreement Generator Column */}
          <div className="bg-black/40 border border-slate-800/80 rounded-lg p-5 flex flex-col gap-4">
            <div className="flex items-center gap-2 font-mono">
              <FileText className="w-4 h-4 text-cyan-400" />
              <span className="text-[10px] font-bold text-cyan-400 uppercase tracking-widest">
                1. Agreement Generator
              </span>
            </div>
            
            <form onSubmit={handleGenerateAgreement} className="space-y-3">
              <div className="flex flex-col gap-1">
                <label className="text-[9px] text-slate-500 font-bold uppercase tracking-widest">Client Name</label>
                <input 
                  type="text" 
                  required
                  placeholder="e.g. Apex Global Syndicate"
                  value={billingClient} 
                  onChange={e => setBillingClient(e.target.value)}
                  className="bg-black/50 border border-slate-800 rounded px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-indigo-500" 
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[9px] text-slate-500 font-bold uppercase tracking-widest">Setup Fee (Lump Sum £)</label>
                <input 
                  type="number" 
                  required
                  placeholder="e.g. 15000"
                  value={billingSetupFee} 
                  onChange={e => setBillingSetupFee(e.target.value)}
                  className="bg-black/50 border border-slate-800 rounded px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-indigo-500" 
                />
              </div>
              <button 
                type="submit" 
                className="w-full py-2 bg-indigo-900/40 hover:bg-slate-800 text-indigo-300 border border-indigo-500/30 rounded font-mono text-[10px] uppercase font-bold tracking-widest transition-all cursor-pointer"
              >
                GENERATE AGREEMENT COVENANT
              </button>
            </form>

            {generatedAgreement && (
              <div className="border border-indigo-900/50 bg-[#040815]/90 rounded p-4 font-mono text-[10px] space-y-3 relative overflow-hidden">
                <div className="flex justify-between items-center border-b border-indigo-950 pb-2">
                  <span className="text-indigo-400 font-bold">NEXORAX COVENANT</span>
                  <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded ${isAgreementSealed ? 'bg-emerald-950/80 text-emerald-400 border border-emerald-500/30' : 'bg-amber-950/80 text-amber-400 border border-amber-500/30'}`}>
                    {generatedAgreement.status}
                  </span>
                </div>
                
                <div className="space-y-1 text-slate-300">
                  <div><span className="text-slate-500 uppercase font-bold text-[8px] tracking-wider block">PROPRIETARY ENTITY:</span> NEXORAX DIGITAL LTD (SC887095)</div>
                  <div><span className="text-slate-500 uppercase font-bold text-[8px] tracking-wider block">CLIENT TARGET:</span> {generatedAgreement.client}</div>
                  <div><span className="text-slate-500 uppercase font-bold text-[8px] tracking-wider block">LUMP SUM SETUP FEE:</span> {generatedAgreement.setupFee}</div>
                  <div><span className="text-slate-500 uppercase font-bold text-[8px] tracking-wider block">PERFORMANCE ROYALTY:</span> {generatedAgreement.royaltyStructure}</div>
                </div>

                {!isAgreementSealed ? (
                  <button 
                    onClick={handleApplySeal}
                    className="w-full py-1.5 bg-amber-500/20 hover:bg-amber-500 text-amber-300 hover:text-black border border-amber-500/40 rounded text-[9px] uppercase font-bold tracking-widest transition-all mt-2 cursor-pointer shadow-[0_0_15px_rgba(245,158,11,0.1)] hover:shadow-[0_0_15px_rgba(245,158,11,0.3)]"
                  >
                    APPLY CHAIR CORONET SEAL
                  </button>
                ) : (
                  <div className="text-emerald-400 font-bold uppercase tracking-widest text-[9px] bg-emerald-950/30 border border-emerald-500/20 p-2 rounded text-center animate-pulse">
                    ✓ ENFORCED BY NODE-83 ENCRYPTION KEY
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Royalty Reconciler Column */}
          <div className="bg-black/40 border border-slate-800/80 rounded-lg p-5 flex flex-col gap-4">
            <div className="flex items-center gap-2 font-mono">
              <Scale className="w-4 h-4 text-indigo-400" />
              <span className="text-[10px] font-bold text-indigo-400 uppercase tracking-widest font-mono">
                2. 10% Maintenance & Hosting Fee Royalty Reconciler
              </span>
            </div>

            <form onSubmit={handleReconcileRoyalty} className="space-y-3">
              <div className="flex flex-col gap-1">
                <label className="text-[9px] text-slate-500 font-bold uppercase tracking-widest">Gross Generated Revenue (£)</label>
                <input 
                  type="number" 
                  required
                  value={reconcileGross} 
                  onChange={e => setReconcileGross(e.target.value)}
                  className="bg-black/50 border border-slate-800 rounded px-3 py-2 text-xs font-mono text-emerald-400 focus:outline-none focus:border-indigo-500" 
                />
              </div>
              <button 
                type="submit" 
                className="w-full py-2 bg-emerald-950/40 hover:bg-slate-800 text-emerald-400 border border-emerald-500/30 rounded font-mono text-[10px] uppercase font-bold tracking-widest transition-all cursor-pointer"
              >
                RECONCILE PERFORMANCE ROYALTY
              </button>
            </form>

            {reconciliationResult && (
              <div className="border border-emerald-900/50 bg-[#04150c]/70 rounded p-4 font-mono text-[10px] space-y-3">
                <div className="flex justify-between items-center border-b border-emerald-950 pb-2">
                  <span className="text-emerald-400 font-bold font-mono tracking-widest uppercase">AUDIT RECEIPT</span>
                  <span className="text-[8px] bg-emerald-950 text-emerald-400 border border-emerald-500/40 font-bold px-1 py-0.5 rounded uppercase">
                    {reconciliationResult.ledgerStatus}
                  </span>
                </div>

                <div className="space-y-2 text-slate-300">
                  <div className="flex justify-between border-b border-slate-800 pb-1">
                    <span className="text-slate-500 uppercase font-black text-[8px] tracking-wider">Gross Transaction:</span>
                    <span className="text-white font-bold">£{reconciliationResult.gross.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-800 pb-1">
                    <span className="text-violet-400 uppercase font-black text-[8px] tracking-wider">20% 10% Maintenance & Hosting Fee Royalty:</span>
                    <span className="text-violet-300 font-extrabold">-£{reconciliationResult.gavelFeeCaptured.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-800 pb-1">
                    <span className="text-emerald-400 uppercase font-black text-[8px] tracking-wider">Net to Client Settlement:</span>
                    <span className="text-emerald-300 font-bold">£{reconciliationResult.netToClient.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                  </div>
                  <div className="flex justify-between text-[8px] gap-2">
                    <span className="text-slate-500 uppercase font-bold">CORE DEPOSIT ADDR:</span>
                    <span className="text-yellow-500 block">Secure Payment Provider (Verified Payment Configuration | Secure Billing Provider)</span>
                  </div>
                </div>

                <div className="text-[7.5px] text-slate-500 select-all font-mono tracking-tighter break-all">
                  SHA-256 SECURED CHECKSUM: {btoa(JSON.stringify(reconciliationResult)).substring(0, 48).toUpperCase()}
                </div>
              </div>
            )}
          </div>

        </div>
      </div>

      {/* GLOBAL CLIENT RECORD & INVOICE PROTOCOL SECTION */}
      <div className="border border-emerald-500/30 bg-[#02050c]/95 rounded-xl p-6 mt-8 flex flex-col gap-6 relative overflow-hidden shadow-2xl">
        <div className="absolute top-0 right-0 p-1 bg-gradient-to-l from-emerald-500/25 to-transparent text-[8px] font-mono text-emerald-400 font-bold uppercase tracking-widest px-4 border-l border-b border-emerald-500/10">
          GLOBAL REGISTRY NODE-07
        </div>

        <div className="flex items-center gap-3 border-b border-emerald-950 pb-4">
          <div className="p-2 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
            <Lock className="w-5 h-5 text-emerald-400 animate-pulse" />
          </div>
          <div>
            <h4 className="text-white text-md font-extrabold tracking-widest uppercase flex items-center gap-2">
              GLOBAL CLIENT RECORD & UNIQUE INVOICE PROTOCOL
              <span className="text-[8px] bg-emerald-950 text-emerald-400 border border-emerald-500/30 px-2 py-0.5 rounded font-mono">TRUTH-ONLY</span>
            </h4>
            <p className="text-slate-400 font-mono text-[9px] uppercase tracking-wider mt-0.5">
              Protocol Mandate: Universal targeting, Mandatory Data Capture, Unique Invoicing References
            </p>
          </div>
        </div>

        {globalSuccess && (
          <div className="p-3 bg-emerald-950/40 border border-emerald-500/30 text-emerald-400 font-mono text-[10px] uppercase tracking-wider rounded flex items-center gap-2 animate-pulse">
            <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
            <span>[PROTOCOL SUCCESS] {globalSuccess}</span>
          </div>
        )}

        {globalError && (
          <div className="p-3 bg-red-950/40 border border-red-500/30 text-red-400 font-mono text-[10px] uppercase tracking-wider rounded flex items-center gap-2">
            <AlertOctagon className="w-4 h-4 text-red-400 shrink-0" />
            <span>[MANDATE WARNING] {globalError}</span>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Form to Capture Mandatory Corporate Records */}
          <div className="col-span-1 bg-black/50 border border-slate-900 rounded-lg p-5 flex flex-col gap-4">
            <div className="font-mono text-[10px] font-bold text-slate-400 uppercase tracking-widest border-b border-slate-900 pb-2 flex items-center gap-1.5 text-glow">
              <User className="w-3.5 h-3.5 text-emerald-400" />
              <span>Mandatory Corporate Record Entry</span>
            </div>

            <form onSubmit={handleRegisterGlobalDeal} className="space-y-3.5">
              <div className="flex flex-col gap-1">
                <label className="text-[8px] text-slate-500 font-bold uppercase tracking-widest">Company Corporate Name *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Paramount Logistics"
                  value={regCompanyName}
                  onChange={e => setRegCompanyName(e.target.value)}
                  className="bg-black/60 border border-slate-800 rounded px-3 py-1.5 text-xs font-mono text-white focus:outline-none focus:border-emerald-500"
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-[8px] text-slate-500 font-bold uppercase tracking-widest">Registered Corporate Address *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. 52 Colmore Row, Birmingham"
                  value={regAddress}
                  onChange={e => setRegAddress(e.target.value)}
                  className="bg-black/60 border border-slate-800 rounded px-3 py-1.5 text-xs font-mono text-white focus:outline-none focus:border-emerald-500"
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-[8px] text-slate-500 font-bold uppercase tracking-widest">Primary Contact Person *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Eleanor Vance"
                  value={regContactName}
                  onChange={e => setRegContactName(e.target.value)}
                  className="bg-black/60 border border-slate-800 rounded px-3 py-1.5 text-xs font-mono text-white focus:outline-none focus:border-emerald-500"
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-[8px] text-slate-500 font-bold uppercase tracking-widest">Invoice Recipient Email *</label>
                <input
                  type="email"
                  required
                  placeholder="e.g. finance@vanceholdings.co.uk"
                  value={regEmail}
                  onChange={e => setRegEmail(e.target.value)}
                  className="bg-black/60 border border-slate-800 rounded px-3 py-1.5 text-xs font-mono text-white focus:outline-none focus:border-emerald-500"
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-[8px] text-slate-500 font-bold uppercase tracking-widest">Invoice Value (GBP £) *</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  placeholder="e.g. 24500"
                  value={regAmount}
                  onChange={e => setRegAmount(e.target.value)}
                  className="bg-black/60 border border-slate-800 rounded px-3 py-1.5 text-xs font-mono text-emerald-400 focus:outline-none focus:border-emerald-500"
                />
              </div>

              <button
                type="submit"
                className="w-full py-2 bg-emerald-900/30 hover:bg-emerald-500 hover:text-black border border-emerald-500/40 rounded font-mono text-[9px] uppercase font-black tracking-widest transition-all cursor-pointer shadow-[0_0_15px_rgba(16,185,129,0.1)]"
              >
                COMMIT PROTOCOL COVENANT
              </button>
            </form>
          </div>

          {/* Interactive Unique Invoices Ledger */}
          <div className="col-span-2 bg-black/50 border border-slate-900 rounded-lg p-5 flex flex-col gap-4">
            <div className="font-mono text-[10px] font-bold text-slate-400 uppercase tracking-widest border-b border-slate-900 pb-2 flex justify-between items-center">
              <span>Verified System Invoices Coded Ledger</span>
              <span className="text-[8px] text-emerald-400 font-black tracking-widest animate-pulse">[ONLINE]</span>
            </div>

            <div className="overflow-y-auto max-h-[350px] space-y-3 pr-1">
              {globalRecords.map((rec) => (
                <div key={rec.invoiceRef} className="border border-slate-800/80 hover:border-emerald-500/20 bg-black/40 p-3 rounded-lg flex flex-col md:flex-row justify-between gap-3 text-xs font-mono transition-all">
                  <div className="space-y-2">
                    <div className="flex items-center gap-1.5">
                      <span className="text-cyan-400 font-black uppercase text-[10px] bg-cyan-950/20 px-2 py-0.5 border border-cyan-500/20 rounded-md">
                        {rec.invoiceRef}
                      </span>
                      <span className="text-white font-extrabold text-[11px] tracking-tight">{rec.companyName}</span>
                    </div>

                    <div className="space-y-1 text-slate-400 text-[10px] pl-1">
                      <div className="flex items-center gap-1"><MapPin className="w-3 h-3 text-slate-500" /> {rec.address}</div>
                      <div className="flex items-center gap-1"><User className="w-3 h-3 text-slate-500" /> {rec.contactName} ({rec.email})</div>
                      <div className="text-[9px] text-slate-500">Coded Date: {new Date(rec.timestamp).toLocaleDateString()} at {new Date(rec.timestamp).toLocaleTimeString()}</div>
                    </div>
                  </div>

                  <div className="flex flex-col justify-between items-end gap-2 shrink-0">
                    <div className="text-right">
                      <div className="text-emerald-400 font-black text-sm">
                        £{rec.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </div>
                      <span className={`inline-block px-2 py-0.5 text-[8px] font-bold tracking-widest uppercase rounded mt-1 border ${
                        rec.status === 'SETTLED' 
                          ? 'bg-emerald-950/60 text-emerald-400 border-emerald-500/30' 
                          : 'bg-amber-950/60 text-amber-500 border-amber-500/30 animate-pulse'
                      }`}>
                        {rec.status}
                      </span>
                    </div>

                    {rec.status !== 'SETTLED' && (
                      <button
                        type="button"
                        onClick={() => handleTogglePaid(rec.invoiceRef)}
                        className="px-2.5 py-1 bg-emerald-500 text-black border border-emerald-400 rounded text-[8px] uppercase tracking-widest font-black transition-colors cursor-pointer"
                        title="Enforce Settlement Action to update metrics securely"
                      >
                        Enforce Settlement
                      </button>
                    )}
                  </div>
                </div>
              ))}

              {globalRecords.length === 0 && (
                <div className="py-16 text-center text-slate-600 uppercase text-[9px] tracking-widest border border-dashed border-slate-900 rounded">
                  No registered active global records coded. Complete details to register above.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
